import { type ReactElement, useState } from 'react';
import { Link } from 'react-router';

import { LinkedAccounts } from '@/features/social-accounts';
import { WithdrawSection } from '@/features/withdraw';
import { useSession } from '@/entities/session';

import { MyShell } from '../../shell';
import { SignedOutNotice } from '../../signed-out-notice';
import { MarketingConsentSection } from './marketing-consent-section';
import { ProfileSection } from './profile-section';

/**
 * 계정 — 로그인 수단과 탈퇴.
 *
 * 수신 설정과 나눈 이유: 저쪽은 "무엇을 받을지"이고 여기는 **"계정 자체를 어떻게 할지"**다.
 * 둘 다 되돌리기 어려운 조치라는 점에서도 같은 묶음이다 — 탈퇴는 되돌릴 수 없고,
 * 소셜 연결은 그 계정으로 계속 들어올 수 있게 된다.
 *
 * 탈퇴가 끝나면 세션이 비므로 `member` 만 보고 그리면 「로그인이 필요합니다」가 뜬다 —
 * 방금 스스로 탈퇴한 사람에게 할 말이 아니다. 그래서 끝났다는 사실을 따로 기억한다.
 */
export function MyAccountPage(): ReactElement {
  const member = useSession((state) => state.member);
  const [isWithdrawn, setWithdrawn] = useState(false);

  if (isWithdrawn) {
    return (
      <div className="u-container flex max-w-[30rem] flex-col items-center gap-5 py-24 text-center">
        <h1 className="text-[1.375rem] font-bold tracking-[-0.04em]">탈퇴가 완료되었습니다</h1>
        <p className="text-[14px] leading-[1.8] text-muted-foreground">
          그동안 이용해 주셔서 감사합니다. 같은 이메일로 다시 가입하실 수 있습니다.
        </p>
        <Link
          to="/"
          className="inline-flex h-11 items-center rounded-md bg-primary px-5 text-sm font-semibold text-primary-foreground hover:bg-primary/85"
        >
          첫 화면으로
        </Link>
      </div>
    );
  }

  if (!member) return <SignedOutNotice />;

  return (
    <MyShell title="계정">
      <div className="flex max-w-[36rem] flex-col gap-8">
        <ProfileSection />
        <MarketingConsentSection />
        <LinkedAccounts />
        <WithdrawSection onWithdrawn={() => setWithdrawn(true)} />
      </div>
    </MyShell>
  );
}
